import type {ReactNode} from 'react';
import type {
  PatientEncounterRow,
  PatientIdentityRow,
  PlannerTypePill,
} from './PatientBrowserCard';
import {buildPatientBrowserIdentity} from './PatientBrowserCard';
import {clean, plannerPatientMetadataParts} from './plannerModels';
import {
  formatPlannerSidebarDateColumn,
  plannerEventDate,
} from './plannerDateTime';
import {
  formatPlannerSidebarTime,
  plannerNavigationAriaLabel,
  plannerNavigationContextText,
} from './PlannerSidebarList';
import {plannerSearchHighlightNode} from './PlannerSearchHighlight';
import {
  plannerEventSummarySnippet,
  plannerEventSummarySnippetInfo,
} from './plannerSearchSnippets';
import {matchesSearchText} from '../../utils/searchText';
import {patientNameFromRecord} from '../../utils/patientNames';

export const UNLINKED_PATIENT_NAME = 'Unlinked Patient';

export const isUnlinkedPlannerEvent = (event: any) =>
  Boolean(
    event &&
    !event.patient &&
    event.source !== 'phoneCall' &&
    event.source !== 'telephoneEncounter',
  );

export const isEncounterStylePlannerEvent = (event: any) =>
  Boolean(
    event &&
    (event.source === 'encounter' ||
      event.source === 'conversation' ||
      event.source === 'phoneCall' ||
      event.source === 'telephoneEncounter' ||
      event.accentKey === 'interpreter' ||
      event.accentKey === 'scribe' ||
      event.accentKey === 'phone'),
  );

export const plannerEventDisplayName = (event: any) => {
  if (isUnlinkedPlannerEvent(event)) return UNLINKED_PATIENT_NAME;
  if (!event?.patient) return event?.patientName || '';
  const name = patientNameFromRecord(event.patient);
  return (
    name.displayName ||
    name.fullName ||
    name.patientName ||
    event?.patientName ||
    ''
  );
};

export const plannerEventLanguage = (event: any) =>
  isUnlinkedPlannerEvent(event) ? '' : event?.patient?.language || '';

export const plannerEventContextText = (event: any) => {
  if (isUnlinkedPlannerEvent(event)) return '';

  if (event?.patient) {
    return buildPatientBrowserIdentity(event.patient).metadataText;
  }

  const language = plannerEventLanguage(event);
  const detailLine = String(event?.detailLine || '').trim();
  const unlinkedDetail = /no linked patient/i.test(detailLine)
    ? detailLine
    : '';

  return plannerNavigationContextText({
    language,
    context: event?.statusLabel || unlinkedDetail,
  });
};

export const plannerEventAriaLabel = (event: any, {includeTime = true} = {}) =>
  plannerNavigationAriaLabel({
    time: includeTime
      ? event?.time || formatPlannerSidebarTime(plannerEventDate(event))
      : '',
    name: plannerEventDisplayName(event),
    language: plannerEventLanguage(event),
    contextText: plannerEventContextText(event),
  });

export const buildPlannerPatientDisplay = (
  event: any,
  {
    contextNode = undefined,
    includeTimeInAria = true,
    nameNode = undefined,
    searchQuery = '',
    showTimeColumn = true,
    time = '',
  }: {
    contextNode?: ReactNode;
    includeTimeInAria?: boolean;
    nameNode?: ReactNode;
    searchQuery?: string;
    showTimeColumn?: boolean;
    time?: string;
  } = {},
) => {
  const name = plannerEventDisplayName(event);
  const language = plannerEventLanguage(event);
  const contextText = plannerEventContextText(event);
  const nameParts = name.split(/\s+/).filter(Boolean);
  const patient = event?.patient
    ? {
        ...event.patient,
        firstName: event.patient.firstName || nameParts[0] || '',
        lastName:
          event.patient.lastName ||
          (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
        displayName: event.patient.displayName || name,
        fullName: event.patient.fullName || name,
        language: event.patient.language || '',
      }
    : null;
  const resolvedNameNode =
    nameNode ?? plannerSearchHighlightNode(name, searchQuery);
  const resolvedContextNode =
    contextNode ??
    (matchesSearchText(contextText, searchQuery)
      ? plannerSearchHighlightNode(contextText, searchQuery)
      : undefined);
  const identityRow: PatientIdentityRow = patient
    ? {
        kind: 'patient',
        patient,
        metadataNode: resolvedContextNode,
        nameNode: resolvedNameNode,
        time: showTimeColumn ? time : undefined,
      }
    : {
        kind: 'label',
        label: name,
        language,
        metadataLines: contextText
          ? [{text: contextText, node: resolvedContextNode}]
          : [],
        nameNode: resolvedNameNode,
        time: showTimeColumn ? time : undefined,
      };

  return {
    ariaLabel: plannerEventAriaLabel(time ? {...event, time} : event, {
      includeTime: includeTimeInAria,
    }),
    contextNode: resolvedContextNode,
    contextText,
    identityRow,
    language,
    name,
    nameNode: resolvedNameNode,
  };
};

const plannerEventConversationLengthText = (event: any) => {
  const durationLabel = clean(event?.durationLabel);
  if (durationLabel) return durationLabel;

  const recordedMinutes = Number(event?.recordedMinutes);
  if (Number.isFinite(recordedMinutes) && recordedMinutes > 0) {
    return `${Math.max(1, Math.ceil(recordedMinutes))} min`;
  }

  const detailLine = clean(event?.detailLine);
  return /^\d+\s*(?:min|mins|minute|minutes)$/i.test(detailLine)
    ? detailLine
    : '';
};

const plannerEventModalityLabelText = (event: any) => {
  if (event?.isDictation) return 'Dictation';
  if (event?.accentKey === 'phone') return 'Phone';
  if (event?.accentKey === 'interpreter') return 'Interpreter';
  if (event?.accentKey === 'scribe') return 'Scribe';
  return '';
};

// The duration text on its own ("18 min"), so the row can show it next to the
// type pill instead of crammed into a single "18 min • Interpreter" caption.
export const plannerEventDurationText = (event: any) =>
  plannerEventConversationLengthText(event);

// The "type of session" pill. Phone rows get an explicit phone chip; Scribe is
// otherwise assumed, and Interpreter / Dictation are the notable additions.
export const plannerEventTypePill = (event: any): PlannerTypePill | null => {
  if (event?.isDictation) return {label: 'Dictation', tone: 'neutral'};
  if (event?.accentKey === 'phone') return {label: 'Phone', tone: 'neutral'};
  if (event?.accentKey === 'interpreter')
    return {label: 'Interpreter', tone: 'neutral'};
  return null;
};

const PLANNER_MODALITY_LABELS: Record<string, string> = {
  video: 'Video',
  phone: 'Phone',
  in_person: 'In-person',
};

// Delivery modality, read from the stored `modality` when present and otherwise
// inferred from the event's telehealth / phone signals.
export const plannerEventModality = (event: any) => {
  const explicit = clean(event?.modality);
  if (explicit) return explicit;
  // Phone wins over video: a web dial-out encounter carries both a phone
  // signal and a telehealth workflow, and it was delivered over the phone.
  if (
    event?.source === 'phoneCall' ||
    event?.accentKey === 'phone' ||
    event?.isTelephonic === true
  )
    return 'phone';
  if (event?.isTelehealth) return 'video';
  return 'in_person';
};

// A neutral pill naming how the session was delivered (Phone / Video / In
// person). Low-key on purpose — it's context, not an alert. Dictation rows
// get no delivery pill: a solo dictation delivers no visit, so "In-person"
// (the default this would land on) misdescribes it — the Dictation type pill
// carries the row instead.
export const plannerEventModalityPill = (
  event: any,
): PlannerTypePill | null => {
  if (event?.isDictation) return null;
  const label = PLANNER_MODALITY_LABELS[plannerEventModality(event)];
  return label ? {label, tone: 'neutral'} : null;
};

// The row's pill set: modality first (how it happened), then the Interpreter
// addition when present. Both neutral so the row stays calm.
export const plannerEventRowPills = (event: any): PlannerTypePill[] =>
  [plannerEventModalityPill(event), plannerEventTypePill(event)]
    .filter((pill): pill is PlannerTypePill => Boolean(pill))
    .filter(
      (pill, index, pills) =>
        pills.findIndex(
          candidate =>
            candidate.label.toLowerCase() === pill.label.toLowerCase(),
        ) === index,
    );

export const plannerEventConversationMetaText = (event: any) =>
  [
    plannerEventConversationLengthText(event),
    plannerEventModalityLabelText(event),
  ]
    .map(clean)
    .filter(Boolean)
    .join(' • ');

export const buildPlannerEncounterDisplay = (
  event: any,
  {
    mode = 'search',
    progressiveKey = 'planner-encounter',
    index = 0,
    searchQuery = '',
    showDateInTimeColumn = false,
  }: {
    mode?: 'recent' | 'search';
    progressiveKey?: string;
    index?: number;
    searchQuery?: string;
    showDateInTimeColumn?: boolean;
  } = {},
) => {
  const timestamp = plannerEventDate(event);
  const date = showDateInTimeColumn
    ? formatPlannerSidebarDateColumn(timestamp)
    : '';

  if (mode === 'recent') {
    const time = formatPlannerSidebarTime(timestamp);
    const summaryText = plannerEventSummarySnippet(event);
    const conversationMetaText = plannerEventConversationMetaText(event);
    const pills = plannerEventRowPills(event);
    const durationText = plannerEventDurationText(event);
    const hasEncounterDetails = Boolean(
      summaryText || pills.length || durationText,
    );
    const ariaLabelSuffix = [summaryText, conversationMetaText]
      .map(clean)
      .filter(Boolean)
      .join(' ');
    const encounterRows: PatientEncounterRow[] = hasEncounterDetails
      ? [
          {
            badges: [],
            pills,
            meta: durationText || undefined,
            key: `${event?.id || `${progressiveKey}-${index}`}-summary`,
            summary: summaryText || undefined,
            summaryNoOfLines: 2,
            summaryProminent: true,
          },
        ]
      : [];

    return {
      ariaLabelSuffix,
      encounterRows,
      summaryText,
      time,
      timeDetail: date,
      timestamp,
    };
  }

  const time = event?.time || formatPlannerSidebarTime(timestamp);
  const summarySnippet = plannerEventSummarySnippetInfo(event, searchQuery);
  const summaryText = summarySnippet.text;
  const summary = clean(summaryText);
  if (!summary) {
    return {
      ariaLabel: '',
      encounterRow: null,
      encounterRows: [],
      name: '',
      nameNode: null,
      summaryNoOfLines: 0,
      summaryText: '',
      time,
      timeDetail: date,
      timestamp,
    };
  }
  const summaryNode = plannerSearchHighlightNode(summary, searchQuery);
  const summaryNoOfLines = summarySnippet.matchedLineCount > 0 ? undefined : 2;
  const pills = plannerEventRowPills(event);
  const durationText = plannerEventDurationText(event);
  const encounterRow: PatientEncounterRow = {
    badges: [],
    pills,
    meta: durationText || undefined,
    date,
    key: `${event?.id || `${progressiveKey}-${index}`}-encounter-row`,
    summary,
    summaryNoOfLines,
    summaryNode,
    summaryProminent: false,
    summaryWhiteSpace: 'pre-line',
    testId: 'planner-browser-encounter-child-row',
    time,
  };

  return {
    ariaLabel: `View encounter ${time} ${summary}`.trim(),
    encounterRow,
    encounterRows: [encounterRow],
    name: summary,
    nameNode: summaryNode,
    summaryNoOfLines,
    summaryText: summary,
    time,
    timeDetail: date,
    timestamp,
  };
};

export const patientHeaderDetailParts = (header: any) => {
  if (!header) return [];
  // Show the patient's language only — never the dialect — in the metadata line.
  const sourceLabel =
    header.sourceLabel ||
    (header.localPatientId && header.ehrPatientId
      ? 'Linked'
      : header.localPatientId
        ? header.linkedEhrId
          ? 'Linked'
          : 'Local'
        : header.ehrPatientId
          ? 'EHR'
          : '');
  return plannerPatientMetadataParts(
    {
      language: header.language || '',
      dob: header.dob,
      mrn: header.mrn,
    },
    {
      sourceLabel,
    },
  );
};
